import { SpanLoading } from '../fetch-status/span-loading';
import { TextTooltip } from '../text-tooltip';
import { resolveChangeDisplay } from './change-display';
import { cn } from '@/client/utils';
import { formatNumber, parseNumber } from '@/client/utils/format';
import NumberFlow, { continuous, type Format } from '@number-flow/react';
import { useMemo } from 'react';

export type NumberViewProps = {
  value: any;
  decimal?: number | string;
  precision?: number | string;
  isChange?: boolean;
  isLoading?: boolean;
  animated?: boolean;
  className?: string;
  showSign?: boolean;
  compact?: boolean; // Whether to use compact notation (K/M/B/T)
};

export const NumberView = ({
  value,
  decimal = 0,
  precision = 2,
  isChange = false,
  isLoading = false,
  animated = false,
  showSign = true,
  compact = true,
  className,
}: NumberViewProps) => {
  const parseValue = parseNumber(value, Number(decimal), Number(precision));
  const { isUp, finalValue, sign } = resolveChangeDisplay(parseValue, Number(precision), isChange, showSign);
  const parseTooltipValue = parseNumber(value, Number(decimal), -1);
  const format: Format = useMemo(
    () => ({
      style: isChange ? 'percent' : 'decimal',
      signDisplay: isChange && showSign ? 'exceptZero' : 'auto',
      minimumFractionDigits: 0,
      maximumFractionDigits: Number(precision),
    }),
    [isChange, precision, showSign]
  );
  return (
    <TextTooltip tooltip={parseTooltipValue} show={true} asChild>
      <span className={cn(isChange && isUp !== null && (isUp ? 'text-beneficial' : 'text-destructive'), className)}>
        {isLoading && <SpanLoading />}
        {animated && (
          <NumberFlow
            style={{
              // @ts-ignore
              '--number-flow-char-height': '0.55em',
            }}
            className={cn(isLoading ? 'hidden' : '', className)}
            willChange={isLoading}
            isolate={true}
            locales="en-US"
            animated={animated}
            plugins={[continuous]}
            value={finalValue}
            format={format}
          />
        )}
        {!isLoading &&
          !animated &&
          sign + formatNumber(isChange && showSign ? Math.abs(finalValue) : finalValue, 0, Number(precision), ',', compact)}
      </span>
    </TextTooltip>
  );
};
